import {JSrcFields, SRC, srcId, srcRef} from "/@lib@/wsp/src.js";
import {IDatasLcSimpleTransition, ILcProvider, ItemTypeTask, WspMetaUi} from "/@lib@/wsp/wspMetaUi.js";
import {Wsp} from "/@lib@/wsp/wsp.js";
import {IO} from "/@lib@/commons/io/io.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {SrcExecTransition} from "/@back@/wsp/actions/lcTaskActions.js";
import {IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";

//************* LC **************


/** Cycles de vies (NOTE: commun à tous les items de type task ou pas). */
export namespace LIFECYCLE {
	export const UNKNOWN_STATE = "?";
	export const DEFAULT_STATE = "";

	/** Evalue si une transition est disponible pour un item ou une task donnée. */
	export function isTransAvailable(trans: IDatasLcSimpleTransition, lcState: string, itModel: string, lcProv: ILcProvider): boolean {
		if (lcState === trans.targetState) return false; //ce src est déjà dans le state cible de cette transition.
		if (trans.restrictForItemTypes && trans.restrictForItemTypes.indexOf(itModel) < 0) return false;
		if (lcState && !lcProv.getLcStates().has(lcState)) lcState = LIFECYCLE.UNKNOWN_STATE;
		if (trans.restrictFromStates && trans.restrictFromStates.indexOf(lcState || LIFECYCLE.DEFAULT_STATE) < 0) return false;
		//if(trans.excludeFromStates && trans.excludeFromStates.indexOf(lcState||LIFECYCLE.DEFAULT_STATE)>=0) return false;
		return true;
	}

	/**
	 * Evalue si cet item dispose d'un cycle de vie :
	 * - un état est défini sur l'item et si cet état est "", vérifie que cet état vide a un name explicite
	 * - OU une transition est disponible.
	 */
	export function hasLcOnItem(shortDesc: JSrcFields, lcProv: ILcProvider) {
		if (shortDesc.lcSt == null) return false; //le backend ne propose pas le LC (fs)
		if (lcProv.getLcStates() === null) return false; //le wspMetaUi (ie lcProv) ne définit aucun cycle de vie
		if (shortDesc.lcSt) return true; // lc !== "", note même si inconnu (chgt de modèle) on affiche pour permettre de revenir dans un état connu
		if (shortDesc.lcSt === "") {
			//Lc vide est-il configuré comme visible ?
			const emptySt = lcProv.getLcState("");
			if (emptySt && emptySt.name) return true; //pas de lc mais lc vide définit avec un libellé
		}
		for (const tr of lcProv.getLcTransitions().values()) if (isTransAvailable(tr, shortDesc.lcSt, shortDesc.itModel, lcProv)) return true;
		return false;
	}

	export function listTransitions(shortDesc: JSrcFields, lcProv: ILcProvider): IDatasLcSimpleTransition[] {
		const r: IDatasLcSimpleTransition[] = [];
		const trMap = lcProv.getLcTransitions();
		if (trMap) for (const tr of trMap.values()) if (isTransAvailable(tr, shortDesc.lcSt, shortDesc.itModel, lcProv)) r.push(tr);
		return r;
	}

	export function listAllTransitionActions<C extends IShortDescCtx>(lcProv: ILcProvider, overrideExec?: (ctx: C, ev?: Event) => any): SrcExecTransition<C>[] {
		const r: SrcExecTransition<C>[] = [];
		const trMap = lcProv.getLcTransitions();
		if (trMap) for (const tr of trMap.values()) r.push(new SrcExecTransition(tr, lcProv, overrideExec));
		return r;
	}
}

//************* Tasks **************

/** Involvements */
export const enum EInvolvement {
	'none' = 0,
	'executor' = 1,
	'follower' = 2,
	'all' = 3
}

/** Etat d'une tâche. */
export const enum ETaskStage {
	'completed' = 'completed',
	'pending' = 'pending',
	'forthcoming' = 'forthcoming'
}


/** Données initialies à la création d'une task ou à la restauration d'une task. */
export interface JTaskStates extends JLastDatas {

	trans?: string

	ti?: string

	scheduledDt?: number

	deadlinedDt?: number

	resps?: {
		[resp: string]: string[] /* accounts */
	}

	/** Fragment XML correctement formé (ns inclus). */
	desc?: string

	/** Fragment XML correctement formé (ns inclus). */
	cmt?: string
}

/** Actions élémentaires pour modifier une task. */
export interface IUpdateTask {
	toDom(doc: Document): Element
}

export class ExecTrans implements IUpdateTask {
	constructor(public trans: string) {}

	toDom(doc: Document): Element {
		const elt = doc.createElementNS(TASK.TASK_NS, "stk:execTransition");
		elt.setAttribute("transition", this.trans);
		return elt;
	}
}

export class SetTitle implements IUpdateTask {
	constructor(public title: string) {}

	toDom(doc: Document): Element {
		const elt = doc.createElementNS(TASK.TASK_NS, "stk:setTitle");
		elt.setAttribute("newTitle", this.title);
		return elt;
	}
}

export class SetDeadline implements IUpdateTask {
	constructor(public dt: string) {}

	toDom(doc: Document): Element {
		const elt = doc.createElementNS(TASK.TASK_NS, "stk:setDeadline");
		elt.setAttribute("newDt", this.dt);
		return elt;
	}
}

export class SetScheduleDt implements IUpdateTask {
	constructor(public dt: string) {}

	toDom(doc: Document): Element {
		const elt = doc.createElementNS(TASK.TASK_NS, "stk:setScheduledDt");
		elt.setAttribute("newDt", this.dt);
		return elt;
	}
}

export class SetDescription implements IUpdateTask {
	constructor(public root: Element) {}

	toDom(doc: Document): Element {
		return doc.importNode(this.root, true);
	}
}

export class AddComment implements IUpdateTask {
	constructor(public root: Element) {}

	toDom(doc: Document): Element {
		return doc.importNode(this.root, true);
	}
}

export class PutUserResp implements IUpdateTask {
	constructor(public account: string, public resp: string) {}

	toDom(doc: Document): Element {
		const elt = doc.createElementNS(TASK.TASK_NS, "stk:putUser");
		elt.setAttribute("account", this.account);
		elt.setAttribute("resp", this.resp);
		return elt;
	}
}

export class RemoveUserResp implements IUpdateTask {
	constructor(public account: string, public resp: string) {}

	toDom(doc: Document): Element {
		const elt = doc.createElementNS(TASK.TASK_NS, "stk:removeUser");
		elt.setAttribute("account", this.account);
		elt.setAttribute("resp", this.resp);
		return elt;
	}
}

export namespace TASK {

	export function isTask(sd: JSrcFields) {
		return sd && ITEM.isAnnot(sd.srcUri);
	}

	export const TASK_NS = "scenari.eu:task:1.0";
	export const TAG_setDescription = "stk:setDescription";
	export const TAG_addComment = "stk:addComment";

	/** Ne retourne que le srcUri, utile en création de tasks. */
	export async function saveTask(wsp: Wsp, uiContext: HTMLElement, refUri: srcRef, updates: IUpdateTask[] | XMLDocument | Element): Promise<JSrcFields> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		let root: Element;
		if (updates instanceof XMLDocument) {
			root = updates.documentElement;
		} else if (updates instanceof Element) {
			root = updates;
		} else {
			const doc = DOM.sharedXmlDoc();
			root = doc.createElementNS(TASK.TASK_NS, "stk:updateTask");
			for (const updt of updates) {
				const elt = updt.toDom(doc);
				if (elt) root.appendChild(elt);
			}
		}
		if (SRC.isNewSrcUri(refUri)) {
			const itModel = SRC.extractItModelFromNewSrcUri(refUri);
			const opts = {srcNm: itModel.substring(4)/*jump stk_*/ + ".tk"};
			return wsp.wspServer.config.tasksUrl.fetchJson<JSrcFields>(IO.qs("cdaction", "AddTask", "param", wsp.code, "options", CDM.stringify(opts), "fields", "srcUri"), {
				method: 'PUT',
				body: DOM.ser(root)
			});
		} else {
			return wsp.wspServer.config.tasksUrl.fetchJson<JSrcFields>(IO.qs("cdaction", "UpdateTask", "param", wsp.code, "taskRefUri", refUri, "fields", "srcUri"), {
				method: 'PUT',
				body: DOM.ser(root)
			});
		}
	}

	export async function deleteTask(wsp: Wsp, uiContext: HTMLElement, srcIds: srcId[]): Promise<void> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const fd = new FormData();
		fd.append("taskSrcIds", srcIds.join('\t'));
		return wsp.wspServer.config.tasksUrl.fetchVoid(IO.qs("cdaction", "DeleteTasks", "param", wsp.code), {method: "POST", body: fd});
	}

	/** Recherche la description de la tâche, ie le dernier tag stk:setDescription dans le contenu */
	export function getDescriptionFromContent(doc: Document): Element {
		if (doc) {
			let lastUpdt = doc.documentElement.lastElementChild;
			while (lastUpdt) {
				let order = lastUpdt.firstElementChild;
				while (order) {
					if (order.localName === "setDescription" && order.namespaceURI === TASK_NS) return order;
					order = order.nextElementSibling;
				}
				lastUpdt = lastUpdt.previousElementSibling;
			}
		}
		return null;
	}

	/** Recherche la deadline de la task (dernier tag stk:setDeadline dans le contenu) */
	export function getDeadlineFromContent(doc: Document): string {
		if (doc) {
			let lastUpdt = doc.documentElement.lastElementChild;
			while (lastUpdt) {
				let order = lastUpdt.firstElementChild;
				while (order) {
					if (order.localName === "setDeadline" && order.namespaceURI === TASK_NS) return order.getAttribute("newDt");
					order = order.nextElementSibling;
				}
				lastUpdt = lastUpdt.previousElementSibling;
			}
		}
		return null;
	}

	/** Recherche la date de planif de la task (dernier tag stk:setScheduledDt dans le contenu). */
	export function getScheduledDtFromContent(doc: Document): string {
		if (doc) {
			let lastUpdt = doc.documentElement.lastElementChild;
			while (lastUpdt) {
				let order = lastUpdt.firstElementChild;
				while (order) {
					if (order.localName === "setScheduledDt" && order.namespaceURI === TASK_NS) return order.getAttribute("newDt");
					order = order.nextElementSibling;
				}
				lastUpdt = lastUpdt.previousElementSibling;
			}
		}
		return "";
	}

	/** Retourne le ou les users avec une resp donnée à partir de l'historique. */
	export function getUsersFromContent(doc: Document, resp: string): Set<string> {
		const set = new Set<string>();
		if (doc) {
			let updt = doc.documentElement.firstElementChild;
			while (updt) {
				let order = updt.firstElementChild;
				while (order) {
					if (order.localName === "putUser" && order.namespaceURI === TASK_NS) {
						if (order.getAttribute("resp").indexOf(resp) >= 0) set.add(order.getAttribute("account"));
					} else if (order.localName === "removeUser" && order.namespaceURI === TASK_NS) {
						if (order.getAttribute("resp").indexOf(resp) >= 0) set.delete(order.getAttribute("account"));
					}
					order = order.nextElementSibling;
				}
				updt = updt.nextElementSibling;
			}
		}
		return set;
	}

	/** Retourne l'implication d'une liste de users pour une task donnée. */
	export function getInvolvementForAccounts(accounts: string[] | '*', taskDesc: JSrcFields, wspMetaUi: WspMetaUi): EInvolvement {
		if (taskDesc && taskDesc.rspUsrs && accounts && wspMetaUi) {
			let inv: EInvolvement = EInvolvement.none;
			const filterResps = accounts == '*' ? taskDesc.rspUsrs : taskDesc.rspUsrs.filter((involvedUser) => {return accounts.find((account) => account == involvedUser.usr && involvedUser.resp)});
			if (filterResps) {
				const itemType = wspMetaUi.getItemType(taskDesc.itModel);
				filterResps.forEach((rspUsers) => {
					const entryInv = itemType instanceof ItemTypeTask ? TASK.findInvolvementForResps(itemType, rspUsers.resp, taskDesc) : EInvolvement.none;
					inv = inv | entryInv;
				});
				return inv;
			}
		}
		return null;
	}

	export function findInvolvementForResps(taskType: ItemTypeTask, respsCodes: string[], taskDesc: JSrcFields): EInvolvement {
		let inv: EInvolvement = EInvolvement.none;
		if (respsCodes) for (let i = 0; i < respsCodes.length && inv !== EInvolvement.all.valueOf(); i++) {
			const resp = taskType.datas.resps ? taskType.datas.resps.find((pRespDef) => pRespDef.code === respsCodes[i]) : null;
			if (resp && resp.involvementRules) {
				resp.involvementRules.forEach((pRule) => {
					if (pRule.ifState) {
						if (taskDesc && pRule.ifState.test(taskDesc.lcSt))
							inv |= pRule.addInv;
					} else
						inv |= pRule.addInv;
					if (inv === EInvolvement.all.valueOf()) return EInvolvement.all;
				});
			}
		}
		return inv;
	}


	/** Masque un field et son contexte graphique : son label précédent voir ses containers s'ils contiennent la class "childDep". */
	export function hideContextField(field: HTMLElement, hidden: boolean) {
		DOM.setHidden(field, hidden);
		const label = DOM.findPreviousSibling(field, (n: Node): n is HTMLElement => n.localName === "label");
		if (label) DOM.setHidden(label, hidden);
		let parent = field.parentElement;
		while (parent && parent.classList.contains("childDep")) {
			DOM.setHidden(parent, hidden);
			parent = parent.parentElement;
		}
	}

}
